import Phaser from 'phaser';
import { VIEWPORT_W } from '../constants';
import { scrollForPanel } from '../layout';
import { emitGame, GameEvents } from '../events';
import { gameState } from '../state';
import type { createPanelCamera } from './panel-camera';

const QUICK_SWIPE_PX = 24;
const DRAG_START_PX = 8;
const FLICK_VELOCITY = 260;
/** Fraction of panel width dragged before snapping to the other screen. */
const PANEL_COMMIT_RATIO = 0.2;

type PanelCamera = ReturnType<typeof createPanelCamera>;

export function createPanelInput(
  scene: Phaser.Scene,
  panelCamera: PanelCamera,
  onGeodeTap: (pointer: Phaser.Input.Pointer) => void,
  dropAtPointer?: (pointer: Phaser.Input.Pointer) => boolean,
  pickupDrop?: (pointer: Phaser.Input.Pointer) => boolean,
  onTigerTap?: () => boolean,
  isTigerHit?: (pointer: Phaser.Input.Pointer) => boolean,
) {
  let swipeStartX: number | null = null;
  let swipeStartY: number | null = null;
  let swipeStartTime: number | null = null;
  let lastMoveX: number | null = null;
  let lastMoveTime: number | null = null;
  let swipeVelocityX = 0;
  let panelSwipeActive = false;
  let panelTransitionActive = false;
  let blockGeodeTap = false;

  function trackVelocity(pointer: Phaser.Input.Pointer) {
    const now = performance.now();
    if (lastMoveX != null && lastMoveTime != null) {
      const dt = now - lastMoveTime;
      if (dt > 0) {
        swipeVelocityX = ((pointer.x - lastMoveX) / dt) * 1000;
      }
    }
    lastMoveX = pointer.x;
    lastMoveTime = now;
  }

  function resolveTargetPanel(scrollX: number, velocityX = 0, fromPanel = gameState.currentPanel) {
    if (velocityX > FLICK_VELOCITY) return 0 as const;
    if (velocityX < -FLICK_VELOCITY) return 1 as const;

    const home = scrollForPanel(fromPanel);
    const delta = scrollX - home;
    const commit = VIEWPORT_W * PANEL_COMMIT_RATIO;

    if (fromPanel === 0 && delta > commit) return 1;
    if (fromPanel === 1 && delta < -commit) return 0;
    return fromPanel;
  }

  function showPanel(index: 0 | 1, { allowSame = false } = {}) {
    const fromIndex = gameState.currentPanel;
    if (index === fromIndex && !allowSame) return;

    panelTransitionActive = true;
    void panelCamera.panToPanel(index).then(() => {
      panelTransitionActive = false;
      panelCamera.setPanelInstant(index);
      if (index !== fromIndex) {
        gameState.currentPanel = index;
        emitGame(GameEvents.PANEL_CHANGED, index);
      }
    });
  }

  const onPointerDown = (pointer: Phaser.Input.Pointer) => {
    swipeStartX = pointer.x;
    swipeStartY = pointer.y;
    swipeStartTime = performance.now();
    lastMoveX = pointer.x;
    lastMoveTime = swipeStartTime;
    swipeVelocityX = 0;
    panelSwipeActive = false;
    blockGeodeTap = gameState.currentPanel === 1 && (dropAtPointer?.(pointer) ?? false);
  };

  const onPointerMove = (pointer: Phaser.Input.Pointer) => {
    if (swipeStartX === null || swipeStartY === null || panelTransitionActive) return;
    trackVelocity(pointer);
    const dx = pointer.x - swipeStartX;
    const dy = pointer.y - swipeStartY;
    if (!panelSwipeActive && (Math.abs(dx) > DRAG_START_PX || Math.abs(dy) > DRAG_START_PX)) {
      if (Math.abs(dx) > Math.abs(dy) * 1.1) {
        panelSwipeActive = true;
        panelCamera.stopPan();
      }
    }
    if (panelSwipeActive) {
      panelCamera.dragBy(dx);
    }
  };

  function endSwipe(pointer: Phaser.Input.Pointer) {
    if (!panelSwipeActive || swipeStartX === null) return;
    trackVelocity(pointer);
    const dx = pointer.x - swipeStartX;
    const target = resolveTargetPanel(panelCamera.getScrollX(), swipeVelocityX);
    showPanel(target, { allowSame: true });
    panelSwipeActive = false;
    swipeStartX = null;
    return dx;
  }

  const onPointerUp = (pointer: Phaser.Input.Pointer) => {
    if (panelSwipeActive) {
      endSwipe(pointer);
      return;
    }

    if (swipeStartX === null || swipeStartY === null) return;
    const dx = pointer.x - swipeStartX;
    const dy = pointer.y - swipeStartY;
    const elapsed = Math.max(performance.now() - (swipeStartTime ?? performance.now()), 1);
    const flickVelocity = (dx / elapsed) * 1000;
    swipeStartX = null;

    if ((Math.abs(dx) > QUICK_SWIPE_PX || Math.abs(flickVelocity) > FLICK_VELOCITY) && Math.abs(dx) > Math.abs(dy)) {
      showPanel(dx > 0 ? 0 : 1);
      return;
    }

    if (gameState.currentPanel === 1 && Math.abs(dx) <= 18 && Math.abs(dy) <= 18) {
      const wasDropTap = blockGeodeTap;
      blockGeodeTap = false;
      if (pickupDrop?.(pointer) || wasDropTap) return;
      if (isTigerHit?.(pointer) && onTigerTap?.()) return;
      onGeodeTap(pointer);
    } else {
      blockGeodeTap = false;
    }
  };

  scene.input.on('pointerdown', onPointerDown);
  scene.input.on('pointermove', onPointerMove);
  scene.input.on('pointerup', onPointerUp);
  scene.input.on('pointerupoutside', (pointer: Phaser.Input.Pointer) => {
    if (panelSwipeActive) endSwipe(pointer);
  });

  return {
    destroy() {
      scene.input.off('pointerdown', onPointerDown);
      scene.input.off('pointermove', onPointerMove);
      scene.input.off('pointerup', onPointerUp);
    },
  };
}
